/**
 * Lenavio Succession API client.
 *
 * Mirror of ``backend/app/routers/succession.py``. Single source
 * of truth for the types every Succession page imports.
 */
import client from "./client";

export type Criticality = "critical" | "important" | "standard";
export type Readiness =
  | "ready_now"
  | "ready_1yr"
  | "ready_2yr"
  | "development_needed";
export type Risk = "red" | "yellow" | "green";

export interface CandidateSummary {
  id: string;
  candidate_employee_id: string;
  candidate_name: string;
  readiness: Readiness;
  performance_rating: number | null;
  potential_rating: number | null;
}

export interface KeyPosition {
  id: string;
  org_id: string;
  title: string;
  department: string | null;
  current_holder_id: string | null;
  current_holder_name: string | null;
  criticality: Criticality;
  notes: string | null;
  created_at: string;
  updated_at: string;
  candidate_count: number;
  top_candidate_readiness: Readiness | null;
  risk: Risk;
  candidates: CandidateSummary[];
}

export interface KeyPositionCreatePayload {
  title: string;
  department?: string;
  current_holder_id?: string;
  criticality?: Criticality;
  notes?: string;
}

export interface KeyPositionUpdatePayload {
  title?: string;
  department?: string;
  current_holder_id?: string | null;
  criticality?: Criticality;
  notes?: string;
}

export interface SuccessionCandidate {
  id: string;
  org_id: string;
  key_position_id: string;
  candidate_employee_id: string;
  candidate_name: string | null;
  candidate_title: string | null;
  candidate_department: string | null;
  readiness: Readiness;
  strengths: string | null;
  development_areas: string | null;
  performance_rating: number | null;
  potential_rating: number | null;
  nominated_by: string | null;
  nominated_at: string;
  last_reviewed: string | null;
}

export interface CandidateCreatePayload {
  candidate_employee_id: string;
  readiness?: Readiness;
  strengths?: string;
  development_areas?: string;
  performance_rating?: 1 | 2 | 3;
  potential_rating?: 1 | 2 | 3;
}

export interface CandidateUpdatePayload {
  readiness?: Readiness;
  strengths?: string;
  development_areas?: string;
  performance_rating?: 1 | 2 | 3;
  potential_rating?: 1 | 2 | 3;
}

export interface CriticalityBreakdown {
  critical: number;
  important: number;
  standard: number;
}

export interface ReadinessBreakdown {
  ready_now: number;
  ready_1yr: number;
  ready_2yr: number;
  development_needed: number;
}

export interface RiskBreakdown {
  red: number;
  yellow: number;
  green: number;
}

export interface AtRiskPosition {
  position_id: string;
  title: string;
  department: string | null;
  criticality: Criticality;
  candidate_count: number;
  risk: Risk;
  reason: string;
}

export interface SuccessionDashboard {
  total_positions: number;
  positions_with_successors: number;
  positions_with_ready_now: number;
  coverage_pct: number;
  by_criticality: CriticalityBreakdown;
  by_readiness: ReadinessBreakdown;
  by_risk: RiskBreakdown;
  at_risk: AtRiskPosition[];
}

export interface AIRecommendation {
  employee_id: string;
  employee_name: string;
  fit_score: number;
  readiness: Readiness;
  rationale: string;
}

export interface AIRecommendResponse {
  position_id: string;
  recommendations: AIRecommendation[];
  model: string;
}

// ── Endpoints ──

export async function listPositions(): Promise<KeyPosition[]> {
  const res = await client.get<KeyPosition[]>("/succession/positions");
  return res.data;
}

export async function createPosition(
  data: KeyPositionCreatePayload,
): Promise<KeyPosition> {
  const res = await client.post<KeyPosition>("/succession/positions", data);
  return res.data;
}

export async function getPosition(id: string): Promise<KeyPosition> {
  const res = await client.get<KeyPosition>(`/succession/positions/${id}`);
  return res.data;
}

export async function updatePosition(
  id: string,
  data: KeyPositionUpdatePayload,
): Promise<KeyPosition> {
  const res = await client.patch<KeyPosition>(
    `/succession/positions/${id}`,
    data,
  );
  return res.data;
}

export async function deletePosition(id: string): Promise<void> {
  await client.delete(`/succession/positions/${id}`);
}

export async function addCandidate(
  positionId: string,
  data: CandidateCreatePayload,
): Promise<SuccessionCandidate> {
  const res = await client.post<SuccessionCandidate>(
    `/succession/positions/${positionId}/candidates`,
    data,
  );
  return res.data;
}

export async function updateCandidate(
  candidateId: string,
  data: CandidateUpdatePayload,
): Promise<SuccessionCandidate> {
  const res = await client.put<SuccessionCandidate>(
    `/succession/candidates/${candidateId}`,
    data,
  );
  return res.data;
}

export async function deleteCandidate(candidateId: string): Promise<void> {
  await client.delete(`/succession/candidates/${candidateId}`);
}

export async function getDashboard(): Promise<SuccessionDashboard> {
  const res = await client.get<SuccessionDashboard>("/succession/dashboard");
  return res.data;
}

export async function aiRecommend(
  positionId: string,
  extraContext?: string,
): Promise<AIRecommendResponse> {
  const res = await client.post<AIRecommendResponse>(
    "/succession/ai-recommend",
    {
      key_position_id: positionId,
      extra_context: extraContext,
    },
  );
  return res.data;
}
